<template>
  <TransitionRoot as="template" :show="show">
    <Dialog as="div" class="relative z-[10000]" @close="$emit('cancel')">
      <TransitionChild
        as="template"
        enter="ease-out duration-300"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="ease-in duration-200"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity z-[9999]" />
      </TransitionChild>

      <div class="fixed inset-0 z-[10000] overflow-y-auto">
        <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <TransitionChild
            as="template"
            enter="ease-out duration-300"
            enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enter-to="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leave-from="opacity-100 translate-y-0 sm:scale-100"
            leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <DialogPanel class="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6 z-[10001]">
              <!-- Rest of your modal content remains the same -->
              <div>
                <div class="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-blue-100">
                  <ChatBubbleLeftRightIcon class="h-6 w-6 text-blue-600" aria-hidden="true" />
                </div>
                <div class="mt-3 text-center sm:mt-5">
                  <DialogTitle as="h3" class="text-base font-semibold leading-6 text-black">
                    Ajouter un commentaire
                  </DialogTitle>
                  
                  <!-- Order Information -->
                  <div class="mt-4 bg-gray-50 rounded-lg p-4 text-left">
                    <div class="grid grid-cols-1 gap-3 text-sm">
                      <!-- Order ID -->
                      <div class="flex justify-between items-center border-b pb-2">
                        <span class="font-medium text-black">Commande:</span>
                        <span class="font-bold text-black">#{{ orderId }}</span>
                      </div>
                      
                      <!-- Client -->
                      <div class="flex justify-between items-center border-b pb-2">
                        <span class="font-medium text-black">Client:</span>
                        <span class="font-semibold text-black">{{ orderInfo?.client || 'Non spécifié' }}</span>
                      </div>
                      
                      <!-- Vendeur -->
                      <div class="flex justify-between items-center border-b pb-2">
                        <span class="font-medium text-black">Vendeur:</span>
                        <span class="font-semibold text-black">{{ orderInfo?.seller || 'Non spécifié' }}</span>
                      </div>
                      
                      <!-- Auteur et Date -->
                      <div class="flex justify-between items-center">
                        <span class="font-medium text-black">Création:</span>
                        <div class="text-right">
                          <div class="font-semibold text-black">{{ orderInfo?.author || 'Non spécifié' }}</div>
                          <div class="text-xs text-black">{{ formatDisplayDate(orderInfo?.creationDate) }}</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Comment Input -->
                  <div class="mt-4">
                    <label for="comment" class="block text-sm font-medium leading-6 text-black text-left mb-2">
                      Votre commentaire
                    </label>
                    <textarea
                      id="comment"
                      v-model="comment"
                      rows="4"
                      class="block w-full rounded-md border-0 py-2 text-black shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-black focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
                      placeholder="Saisissez votre commentaire ici..."
                    />
                  </div>
                </div>
              </div>
              <div class="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                <button
                  type="button"
                  class="inline-flex w-full justify-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 sm:col-start-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  :disabled="!comment.trim()"
                  @click="handleSubmit"
                >
                  Ajouter le commentaire
                </button>
                <button
                  type="button"
                  class="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-black shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:col-start-1 sm:mt-0"
                  @click="$emit('cancel')"
                >
                  Annuler
                </button>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue'
import { ChatBubbleLeftRightIcon } from '@heroicons/vue/24/outline'

interface OrderInfo {
  client: string
  seller: string
  author: string
  creationDate: string
}

interface Props {
  show: boolean
  orderId: number | null
  orderInfo?: OrderInfo
}

interface Emits {
  (e: 'submit', data: { comment: string }): void
  (e: 'cancel'): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const comment = ref('')

// Format date for display
const formatDisplayDate = (dateString: string | undefined) => {
  if (!dateString) return 'Date non spécifiée'
  
  try {
    const date = new Date(dateString)
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  } catch {
    return dateString
  }
}

// Reset the comment when modal opens/closes
watch(() => props.show, (newVal) => {
  if (!newVal) {
    comment.value = ''
  }
})

const handleSubmit = () => {
  if (comment.value.trim()) {
    emit('submit', { comment: comment.value.trim() })
  }
}
</script>